import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MessageSquare,
  Plus,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import { BachecaSidePanel } from "@/components/BachecaSidePanel";
import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useCreaBacheca } from "@/hooks/useAdminBacheca";
import { useAdminUtenti } from "@/hooks/useAdminUtenti";
import { useBacheca } from "@/hooks/useBacheca";
import { useMe } from "@/hooks/useMe";
import { useModuli } from "@/hooks/useModuli";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type {
  BachecaTipo,
  BachecaVoce,
  ParteResponsabile,
} from "@/types/api";

const TIPI: { value: BachecaTipo; label: string }[] = [
  { value: "nota", label: "Nota" },
  { value: "decisione", label: "Decisione" },
  { value: "blocco", label: "Blocco" },
];

const PARTI: { value: ParteResponsabile; label: string }[] = [
  { value: "misterpet", label: "Mister Pet" },
  { value: "maitime", label: "Maitime" },
  { value: "entrambi", label: "Entrambi" },
];

type Filtro = "verso_cliente" | "verso_maitime" | "chiuse";

export function AdminBachecaPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { voceId: voceIdFromUrl } = useParams<{ voceId?: string }>();
  const navigate = useNavigate();

  const [filtro, setFiltro] = useState<Filtro>("verso_cliente");
  const [search, setSearch] = useState("");
  const [creaOpen, setCreaOpen] = useState(false);

  const { data: voci = [], isLoading } = useBacheca({});

  const { vociPerTab, counts, voceSelezionata } = useMemo(() => {
    const aperteVersoCliente = voci.filter(
      (v) => v.stato !== "chiuso" && v.parte_responsabile === "misterpet"
    );
    const aperteVersoMaitime = voci.filter(
      (v) =>
        v.stato !== "chiuso" &&
        (v.parte_responsabile === "maitime" || v.parte_responsabile === "entrambi")
    );
    const chiuse = voci.filter((v) => v.stato === "chiuso");

    const counts = {
      verso_cliente: aperteVersoCliente.length,
      verso_maitime: aperteVersoMaitime.length,
      chiuse: chiuse.length,
      non_letti_cliente: aperteVersoCliente.filter((v) => v.ha_messaggi_non_letti).length,
    };

    const lista =
      filtro === "verso_cliente"
        ? aperteVersoCliente
        : filtro === "verso_maitime"
          ? aperteVersoMaitime
          : chiuse;

    const filtrate = search
      ? lista.filter((v) =>
          v.titolo.toLowerCase().includes(search.toLowerCase())
        )
      : lista;

    const ordinate = [...filtrate].sort((a, b) => {
      if (a.ha_messaggi_non_letti !== b.ha_messaggi_non_letti) {
        return a.ha_messaggi_non_letti ? -1 : 1;
      }
      const aTime = a.ultimo_messaggio_il ?? a.created_at;
      const bTime = b.ultimo_messaggio_il ?? b.created_at;
      return bTime.localeCompare(aTime);
    });

    const sel = voceIdFromUrl
      ? voci.find((v) => v.id === voceIdFromUrl) ?? null
      : null;

    return { vociPerTab: ordinate, counts, voceSelezionata: sel };
  }, [voci, filtro, search, voceIdFromUrl]);

  // Quando l'URL cita una voce, allinea il tab di filtro alla sua categoria
  // così la riga è visibile nella lista a sinistra.
  useEffect(() => {
    if (!voceSelezionata) return;
    if (voceSelezionata.stato === "chiuso") setFiltro("chiuse");
    else if (voceSelezionata.parte_responsabile === "misterpet")
      setFiltro("verso_cliente");
    else setFiltro("verso_maitime");
  }, [voceSelezionata]);

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  function apriVoce(voce: BachecaVoce) {
    navigate(`/admin/bacheca/${voce.id}`);
  }

  function chiudiPanel() {
    navigate("/admin/bacheca");
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Backoffice Maitime
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Bacheca
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl pt-1">
            Gestione delle voci di bacheca: domande aperte, decisioni, blocchi. Solo
            Maitime chiude le voci.
          </p>
        </div>
        <Button onClick={() => setCreaOpen(true)}>
          <Plus className="size-4" />
          Nuova voce
        </Button>
      </header>

      <Tabs value={filtro} onValueChange={(v) => setFiltro(v as Filtro)}>
        <TabsList>
          <TabsTrigger value="verso_cliente" className="gap-2">
            Verso cliente
            <Badge variant="outline" className="text-[10px]">
              {counts.verso_cliente}
            </Badge>
            {counts.non_letti_cliente > 0 && (
              <span className="size-1.5 rounded-full bg-destructive" title="Messaggi non letti" />
            )}
          </TabsTrigger>
          <TabsTrigger value="verso_maitime" className="gap-2">
            Verso Maitime
            <Badge variant="outline" className="text-[10px]">
              {counts.verso_maitime}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="chiuse" className="gap-2">
            Chiuse
            <Badge variant="outline" className="text-[10px]">
              {counts.chiuse}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={filtro} className="space-y-3">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per titolo…"
              className="w-full rounded-md border bg-background py-2 pl-9 pr-9 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>

          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : vociPerTab.length === 0 ? (
            <div className="rounded-lg border bg-card/40 p-6 text-center text-sm text-muted-foreground">
              Nessuna voce in questa categoria.
            </div>
          ) : (
            <div className="rounded-lg border bg-card divide-y">
              {vociPerTab.map((v) => (
                <VoceRow
                  key={v.id}
                  voce={v}
                  selected={voceSelezionata?.id === v.id}
                  onOpen={() => apriVoce(v)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <BachecaSidePanel
        voce={voceSelezionata}
        parteUtente="maitime"
        mode="admin"
        onClose={chiudiPanel}
      />

      <CreaDialog open={creaOpen} onClose={() => setCreaOpen(false)} />
    </PageContainer>
  );
}

// ── Riga lista ──────────────────────────────────────────────────────

function VoceRow({
  voce,
  selected,
  onOpen,
}: {
  voce: BachecaVoce;
  selected: boolean;
  onOpen: () => void;
}) {
  const tipoLabel = TIPI.find((t) => t.value === voce.tipo)?.label ?? voce.tipo;
  const parteLabel =
    PARTI.find((p) => p.value === voce.parte_responsabile)?.label ?? "";

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "w-full text-left px-4 py-3 transition-colors flex items-start gap-3",
        selected ? "bg-accent/40 border-l-2 border-l-primary" : "hover:bg-muted/40"
      )}
    >
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] px-1.5">
            {tipoLabel}
          </Badge>
          <h3 className="text-sm font-medium truncate flex-1">{voce.titolo}</h3>
          {voce.ha_messaggi_non_letti && (
            <span className="size-2 rounded-full bg-destructive shrink-0" title="Messaggi non letti" />
          )}
        </div>
        {voce.corpo && (
          <p className="text-xs text-muted-foreground line-clamp-1">{voce.corpo}</p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
          <span>verso {parteLabel}</span>
          {voce.assegnato_a_nome && (
            <Badge
              variant="outline"
              className="text-[10px] bg-brand-50 text-brand-700 border-brand-200"
            >
              @ {voce.assegnato_a_nome}
            </Badge>
          )}
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="size-3" />
            {voce.n_messaggi}
          </span>
          {voce.ultimo_messaggio_il && (
            <span>ultimo: {formatDataOra(voce.ultimo_messaggio_il)}</span>
          )}
          {voce.stato === "chiuso" && voce.chiuso_il && (
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 className="size-3" /> chiusa {formatData(voce.chiuso_il)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Dialog crea ──────────────────────────────────────────────────────

function CreaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [titolo, setTitolo] = useState("");
  const [corpo, setCorpo] = useState("");
  const [tipo, setTipo] = useState<BachecaTipo>("nota");
  const [parte, setParte] = useState<ParteResponsabile>("misterpet");
  const [moduloId, setModuloId] = useState<string | null>(null);
  const [visCliente, setVisCliente] = useState(true);
  const [assegnatoId, setAssegnatoId] = useState<string | null>(null);
  const { data: moduli = [] } = useModuli();
  const { data: utenti = [] } = useAdminUtenti();
  const mutation = useCreaBacheca();

  const destinatariCandidati = utenti
    .filter((u) => u.attivo && u.parte === "misterpet" && u.utente_id)
    .sort((a, b) =>
      (a.nome_completo ?? a.email).localeCompare(b.nome_completo ?? b.email)
    );

  function reset() {
    setTitolo("");
    setCorpo("");
    setTipo("nota");
    setParte("misterpet");
    setModuloId(null);
    setVisCliente(true);
    setAssegnatoId(null);
    mutation.reset();
  }

  function salva() {
    if (!titolo.trim()) return;
    mutation.mutate(
      {
        tipo,
        titolo: titolo.trim(),
        corpo: corpo.trim() || null,
        parte_responsabile: parte,
        modulo_id: moduloId,
        is_visibile_cliente: visCliente,
        assegnato_a_id: assegnatoId,
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
      }
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Nuova voce di bacheca</DialogTitle>
          <DialogDescription className="text-xs">
            Crea una nota, decisione o blocco. Se "Verso Mister Pet" e visibile,
            apparirà nella card "Cosa serve da te" del portale cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <FormField label="Titolo *">
            <input
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Es. Confermare unità di misura quantità ordini"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              autoFocus
            />
          </FormField>
          <FormField label="Corpo (opzionale)">
            <Textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              rows={4}
              placeholder="Spiegazione, contesto, link eventuali…"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Tipo">
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as BachecaTipo)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {TIPI.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Verso">
              <select
                value={parte}
                onChange={(e) => setParte(e.target.value as ParteResponsabile)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {PARTI.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <FormField label="Modulo (opzionale)">
            <select
              value={moduloId ?? ""}
              onChange={(e) => setModuloId(e.target.value || null)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">— nessuno —</option>
              {moduli.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.codice} · {m.nome}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Rivolta a (opzionale)">
            <select
              value={assegnatoId ?? ""}
              onChange={(e) => setAssegnatoId(e.target.value || null)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">— tutti i destinatari Mister Pet —</option>
              {destinatariCandidati.map((u) => (
                <option key={u.email} value={u.utente_id!}>
                  {u.nome_completo ?? u.email}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Se valorizzato, solo l'utente destinatario (più Maitime + ruoli
              Presidenza/Management) vede la voce.
            </p>
          </FormField>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={visCliente}
              onChange={(e) => setVisCliente(e.target.checked)}
            />
            Visibile al cliente
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button onClick={salva} disabled={mutation.isPending || !titolo.trim()}>
            {mutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Crea voce
          </Button>
        </DialogFooter>
        {mutation.isError && (
          <p className="text-xs text-destructive">
            Errore: {(mutation.error as Error).message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function formatData(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "short",
  });
}

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
